import { useMemo, useState } from "react";
import { num, rupee } from "../format";
import type { Tradebook } from "../types";

/** The risk levels worth seeing side by side. A full-size bet is rarely right. */
const RISK_LADDER = [1, 0.75, 0.5, 0.3, 0.2];

interface Row {
  riskPct: number;
  quantity: number;
  positionValue: number;
  positionPct: number;
  riskRupees: number;
  actualRiskPct: number;
  cappedByExposure: boolean;
  reward: number | null;
  rewardPctOfCapital: number | null;
}

/**
 * Work out size from risk rather than from gut feel.
 *
 * Shown as a ladder rather than one number: conviction varies, and the useful
 * question at entry is "what does this cost me at each level of commitment",
 * not "what does a fixed 1% allow". Seeing the alternatives together is what
 * makes taking a half-size position a deliberate choice instead of an
 * afterthought.
 *
 * Both caps are computed because they bind at different times — the risk budget
 * when the stop is far away, the position limit when it is close.
 */
export function PositionSizer({
  book,
  compact,
  onApply,
}: {
  book: Tradebook;
  compact?: boolean;
  onApply?: (quantity: number, entry: number, stop: number) => void;
}) {
  const [entry, setEntry] = useState("");
  const [stop, setStop] = useState("");
  const [target, setTarget] = useState("");
  // Overridable: sizing against a hypothetical account is the whole point of a
  // calculator. Empty means "use the book's own capital".
  const [capitalOverride, setCapitalOverride] = useState("");

  const result = useMemo(() => {
    const e = Number(entry) || 0;
    const s = Number(stop) || 0;
    const t = Number(target) || 0;
    const overridden = capitalOverride.trim() !== "" && Number(capitalOverride) > 0;
    const capital = overridden ? Number(capitalOverride) : book.capital_base;

    if (e <= 0 || s <= 0 || capital <= 0) return null;

    const riskPerShare = e - s;
    if (riskPerShare <= 0) {
      return { invalid: "Your stop is at or above the entry price." as const };
    }

    // The book caps how much of the account one position may occupy. This does
    // not scale with the risk level, so at tight stops it binds every row.
    const byExposure = Math.floor((capital * (book.max_position_pct / 100)) / e);

    // The book's own limit is included even when it isn't a round number, so
    // the configured setting is never missing from its own calculator.
    const levels = [...new Set([...RISK_LADDER, book.risk_per_trade_pct])].sort(
      (a, b) => b - a,
    );

    const rows: Row[] = levels.map((riskPct) => {
      const byRisk = Math.floor((capital * (riskPct / 100)) / riskPerShare);
      const quantity = Math.max(0, Math.min(byRisk, byExposure));
      const riskRupees = quantity * riskPerShare;
      const positionValue = quantity * e;
      const reward = t > 0 ? quantity * (t - e) : null;
      return {
        riskPct,
        quantity,
        positionValue,
        positionPct: (positionValue / capital) * 100,
        riskRupees,
        actualRiskPct: (riskRupees / capital) * 100,
        cappedByExposure: byExposure < byRisk,
        reward,
        // The R multiple and the per-share gain are identical on every row —
        // they depend on the prices, not the size. What actually changes with
        // size is what the move is worth against the account.
        rewardPctOfCapital: reward === null ? null : (reward / capital) * 100,
      };
    });

    return {
      rows,
      riskPerShare,
      stopPct: (riskPerShare / e) * 100,
      rr: t > 0 ? (t - e) / riskPerShare : null,
      targetMovePct: t > 0 ? ((t - e) / e) * 100 : null,
      byExposure,
      capital,
      overridden,
    };
  }, [entry, stop, target, capitalOverride, book]);

  const body = (
    <>
      <div className="grid cols-4">
        <div>
          <label>Capital</label>
          <input
            type="number"
            step="10000"
            value={capitalOverride}
            placeholder={String(book.capital_base)}
            onChange={(e) => setCapitalOverride(e.target.value)}
          />
        </div>
        <div>
          <label>Entry Price</label>
          <input
            type="number"
            step="0.05"
            value={entry}
            onChange={(e) => setEntry(e.target.value)}
          />
        </div>
        <div>
          <label>Stop Loss</label>
          <input
            type="number"
            step="0.05"
            value={stop}
            onChange={(e) => setStop(e.target.value)}
          />
        </div>
        <div>
          <label>Target (optional)</label>
          <input
            type="number"
            step="0.05"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
        </div>
      </div>

      {result && "invalid" in result && (
        <div className="banner warn" style={{ marginTop: 14 }}>
          ⚠ {result.invalid}
        </div>
      )}

      {result && !("invalid" in result) && (
        <>
          <div className="sizer-summary">
            <span>
              Risk per share <strong>{rupee(result.riskPerShare)}</strong>
            </span>
            <span>
              Stop is <strong>{num(result.stopPct)}%</strong> below entry
            </span>
            {result.rr !== null && (
              <span className={result.rr >= 2 ? "pos" : "neg"}>
                Reward:Risk <strong>1:{num(result.rr, 1)}</strong>
                {result.rr < 2 && " — thin"}
              </span>
            )}
            {result.targetMovePct !== null && (
              <span>
                Target is <strong>{num(result.targetMovePct)}%</strong> above entry
              </span>
            )}
            <span className={result.overridden ? "" : "muted"}>
              Capital <strong>{rupee(result.capital, 0)}</strong>
              {result.overridden && (
                <>
                  {" "}
                  <button
                    className="ghost"
                    style={{ padding: "0 6px" }}
                    onClick={() => setCapitalOverride("")}
                  >
                    reset to book
                  </button>
                </>
              )}
            </span>
          </div>

          <table className="legs sizer-table">
            <thead>
              <tr>
                <th>Risk %</th>
                <th>Shares</th>
                <th>Position Value</th>
                <th>% of Capital</th>
                <th>Risk if stopped</th>
                {result.rows[0].reward !== null && <th>Reward at target</th>}
                {onApply && <th />}
              </tr>
            </thead>
            <tbody>
              {result.rows.map((row) => (
                <tr
                  key={row.riskPct}
                  className={row.riskPct === book.risk_per_trade_pct ? "default-risk" : ""}
                >
                  <td>
                    <strong>{num(row.riskPct, 2).replace(/\.?0+$/, "")}%</strong>
                    {row.riskPct === book.risk_per_trade_pct && (
                      <span className="badge muted">book default</span>
                    )}
                  </td>
                  <td>
                    <strong>{row.quantity.toLocaleString("en-IN")}</strong>
                    {row.cappedByExposure && (
                      <span
                        className="badge muted"
                        title={`Limited by the ${book.max_position_pct}% position cap, not by risk`}
                      >
                        capped
                      </span>
                    )}
                  </td>
                  <td>{rupee(row.positionValue, 0)}</td>
                  <td>{num(row.positionPct)}%</td>
                  <td className="neg">
                    {rupee(row.riskRupees, 0)}{" "}
                    <span className="muted">({num(row.actualRiskPct)}%)</span>
                  </td>
                  {row.reward !== null && (
                    <td className="pos">
                      {rupee(row.reward, 0)}{" "}
                      <span className="muted">({num(row.rewardPctOfCapital)}%)</span>
                    </td>
                  )}
                  {onApply && (
                    <td>
                      <button
                        disabled={row.quantity === 0}
                        onClick={() => onApply(row.quantity, Number(entry), Number(stop))}
                      >
                        Use
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {result.rows.some((r) => r.cappedByExposure) && (
            <div className="hint">
              Rows marked <em>capped</em> are limited by the {book.max_position_pct}% position
              size limit rather than by risk — the stop is close enough that the risk budget
              would allow more than the book permits in one name. Maximum{" "}
              {result.byExposure.toLocaleString("en-IN")} shares at this price.
            </div>
          )}
          {result.overridden && (
            <div className="hint">
              Sizing against {rupee(result.capital, 0)} instead of {book.name}'s{" "}
              {rupee(book.capital_base, 0)}. This is a what-if only — the risk figures shown
              on trade cards still use the book's own capital. Change that in Settings.
            </div>
          )}
          {result.rows.every((r) => r.quantity === 0) && (
            <div className="banner warn">
              Every level rounds to zero shares — the stop is too wide for this capital at any
              of these risk levels.
            </div>
          )}
        </>
      )}

      {!result && (
        <div className="hint">
          Enter a price and a stop to see how many shares each risk level allows. Capital
          defaults to {book.name}'s {rupee(book.capital_base, 0)} — override it to size
          against a different figure.
        </div>
      )}
    </>
  );

  if (compact) return body;

  return (
    <div className="card">
      <div className="section-title" style={{ marginTop: 0 }}>
        Position Size Calculator · {book.name}
      </div>
      {body}
    </div>
  );
}
